import { useState, useEffect, useCallback } from "react";
import { Box, VStack } from "@chakra-ui/react";

import Header from "./header/Header";
import FirmProSection from "./FirmProSection";

import { getFPSummaries } from "../utils/api";
import generateFirmPros from "./generateFirmPros";
import { DaySearch, FirmPro, ServerStatus } from "../utils/interfaces";


/**
 * La página principal del semáforo.
 * 
 * @returns {JSX.Element} 
 */
export default function HomeScreen(): JSX.Element {
  const [softwareVersion, setSoftwareVersion] = useState("");
  const [serverStatus, setServerStatus] = useState<ServerStatus>({ hasError: false, isLoading: true, unixTime: 0 });
  const [allFirmPros, setAllFirmPros] = useState<FirmPro[]>([]);
  const [daySearch, setDaySearch] = useState<DaySearch>({ enabled: false, isLoading: true, days: 1 });

  // const [reloadKey, setReloadKey] = useState(0);
  
  const allEnabledFirmProIndices: number[] = [];
  const badEnabledFirmProIndices: number[] = [];
  const disabledFirmProIndices: number[] = [];

  /**
   * Obtiene los estados de todos los FirmPros.
   */
  const getAllMeterData = useCallback(() => {
    getFPSummaries(daySearch)
      .then(res => {
        const newAllMeterData = generateFirmPros(res.data.status);
        setServerStatus({ hasError: false, isLoading: false, unixTime: res.data.serverUnixTime });
        setSoftwareVersion(res.data.version);
        setAllFirmPros(newAllMeterData);
        setDaySearch({ enabled: daySearch.enabled, isLoading: false, days: daySearch.days });
      })
      .catch(() => {
        setServerStatus({ hasError: true, isLoading: false, unixTime: serverStatus.unixTime });
      });
  }, [daySearch, serverStatus.unixTime]);

  // const getConfig = useCallback(() => {
  //   let data = { version: "?", config: DEFAULT_CONFIG };
  //   getSemaphoreConfig()
  //     .then(res => data = res.data)
  //     .catch(err => console.log(err))
  //     .finally(() => {
  //       setSoftwareVersion(data.version);
  //       setConfig(data.config);
  //       getAllMeterData();
  //     });
  // }, [getAllMeterData]);

  // useEffect(getConfig, [getConfig]);

  useEffect(() => {
    if (daySearch.isLoading) {
      getAllMeterData();
      return () => {};
    }

    // Si se está buscando más allá en el pasado, es mejor no preguntar cada 30
    // segundos.
    if (!daySearch.enabled) {
      const requestInterval = setInterval(getAllMeterData, 30000);
      return () => clearInterval(requestInterval);
    }
  }, [daySearch, getAllMeterData]);

  /**
   * Recalcula los índices de los equipos FirmPro que se mostrarán en cada una
   * de las secciones.
   * 
   * A veces, es necesario notificar a React que hay que actualizar los
   * equipos. Para ello, se puede actualizar el estado reloadKey si
   * updateReloadKey es true.
   * 
   * @param {boolean} updateReloadKey Determina si actualizar o no el estado
   * reloadKey para forzar a React a actualizar los equipos.
   */
  const reloadDeviceIndices = (updateReloadKey: boolean) => {
    badEnabledFirmProIndices.splice(0, badEnabledFirmProIndices.length);

    allFirmPros.forEach((meter, i) => {
      if (meter.enableAlarms) {
        allEnabledFirmProIndices.push(i);
        // Para la sección de dispositivos caídos (en DeviceSection), obtener todos
        // los dispositivos que tengan algún color amarillo o rojo
        if (meter.boxBGColor.startsWith("yellow") ||
          meter.boxBGColor.startsWith("red") ||
          meter.boxBGColor.startsWith("gray")
        ) {
          badEnabledFirmProIndices.push(i);
        }
      } else {
        disabledFirmProIndices.push(i);
      }
    });

    // Ordenarlos de mayor a menor gravedad de problemas
    badEnabledFirmProIndices.sort((i, j) => {
      const d1 = allFirmPros[i];
      const d2 = allFirmPros[j];
      return (d2.numCriticalIssues - d1.numCriticalIssues) + 0.1*(d2.numWarnings - d1.numWarnings);
    });
    
    // if (updateReloadKey) {
    //   setReloadKey(reloadKey + 1);
    // }
  };

  reloadDeviceIndices(false);

  return (
    <Box w="full" px={2} py={{ "base": 3, "lg": 5 }}>
      <VStack spacing={{ "base": 2, "md": 4, "lg": 6 }} align="center">
        <Header
          softwareVersion={softwareVersion}
          serverStatus={serverStatus}
          numEnabledFirmPros={allEnabledFirmProIndices.length}
          numBadFirmPros={badEnabledFirmProIndices.length}
          daySearch={daySearch}
          setDaySearch={setDaySearch}
        />
        <FirmProSection
          allFirmPros={allFirmPros}
          allEnabledFirmProIndices={allEnabledFirmProIndices}
          badEnabledFirmProIndices={badEnabledFirmProIndices}
          disabledFirmProIndices={disabledFirmProIndices}
          reloadDeviceIndices={reloadDeviceIndices}
        />
      </VStack>
    </Box>
  );
}